/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import { request } from '@mairistem/tools';
import {
  Button,
  Container,
  Dimmer,
  Icon,
  Loader,
  Modal,
  Tab,
  Label,
  Segment,
} from '@mairistem/composants';

import type { ApiResponse, ParametresGeneraux } from '@/types';
import {
  createParametresGeneraux,
  getParametrage,
  updateParametresGeneraux,
  uploadFileParametragesGeneraux,
  downloadFileParametragesGeneraux,
  baseUrlApp,
  pingHubee,
} from '@/routes';

import { DEFAULT_PARAMETRES_GENERAUX } from '@/constants';
import { useEntiteStore } from '@/zustand';
import {
  validationTabCollectivite,
  validationTabRecensement,
} from '@/utils/validations/parametres';
import { useFetchHubeeStatus } from '@/hooks';
import { TabCollectivite } from './TabCollectivite';
import { TabRecensement } from './TabRecensement';
import { TabHubee } from './TabHubee';

interface ModalParametresGenerauxProps {
  onClose: () => void;
  redirect: boolean;
  gestionTrimestre?: boolean;
  gestionHubee?: boolean;
  departement?: any;
}

const ModalParametresGeneraux = (props: ModalParametresGenerauxProps) => {
  const { onClose, redirect, gestionTrimestre, gestionHubee, departement } =
    props;

  const [parametres, setParametres] = React.useState<ParametresGeneraux>(
    DEFAULT_PARAMETRES_GENERAUX,
  );
  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadingImages, setLoadingImages] = React.useState<boolean>(false);
  const [updated, setUpdated] = React.useState(false);
  const [hubeeUpdated, setHubeeUpdated] = React.useState(false);
  const [initialCachet, setInitialCachet] = React.useState<string>();
  const [initialSignature, setInitialSignature] = React.useState<string>();
  const [errors, setErrors] = React.useState<any>({});
  const [activeTabIndex, setActiveTabIndex] = React.useState<number>(
    gestionTrimestre ? 1 : gestionHubee ? 2 : 0,
  );

  const selectedEntite = useEntiteStore((state) => state.selectedEntite);
  const fetchHubee = useFetchHubeeStatus();

  const history = useHistory();

  const { imageCachet, imageSignature } = parametres || {};

  const errorMessage: string = React.useMemo(() => {
    if (_.size(errors) === 1) {
      return _.values(errors)[0] as string;
    }
    if (_.size(errors) > 1) {
      return 'Plusieurs erreurs sont à corriger dans vos données.';
    }
    return null;
  }, [errors]);

  React.useEffect(() => {
    setLoading(true);
    request
      .get(getParametrage())
      .then(async ({ data }: ApiResponse<ParametresGeneraux>) => {
        setLoading(false);
        const parametre: ParametresGeneraux = data?.[0] || null;

        if (!_.isEmpty(parametre) && !_.isNil(parametre)) {
          setParametres(parametre);
        }
      })
      .catch(() => {
        toast(
          'Une erreur est survenue lors de la récupération des paramètres',
          { type: 'error' },
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchImages = React.useCallback(() => {
    setLoadingImages(true);

    Promise.all([
      request.get(
        downloadFileParametragesGeneraux(parametres?.identifiant, 'cachet'),
        { headers: { Accept: '*/*' } },
      ),
      request.get(
        downloadFileParametragesGeneraux(parametres?.identifiant, 'signature'),
        { headers: { Accept: '*/*' } },
      ),
    ])
      .then(([cachet, signature]: Record<string, any>[]) => {
        const imgCachet = _.isString(cachet) ? cachet : cachet.data;
        const imgSignature = _.isString(signature) ? signature : signature.data;

        setInitialCachet(imgCachet);
        setInitialSignature(imgSignature);

        setParametres((prev) => ({
          ...prev,
          imageCachet: imgCachet,
          imageSignature: imgSignature,
        }));
      })
      .catch(() => {
        toast('Erreur lors du chargement des images', { type: 'error' });
      })
      .finally(() => {
        setLoadingImages(false);
      });
  }, [parametres?.identifiant]);

  React.useEffect(() => {
    if (!_.isNil(parametres?.identifiant)) {
      fetchImages();
    }
  }, [fetchImages, parametres?.identifiant]);

  const handleChange = React.useCallback(
    (name, value) => {
      setParametres((prev) => {
        const newParametres = _.cloneDeep(prev);

        _.set(newParametres, name, value);

        return newParametres;
      });

      if (!updated) setUpdated(true);
      if (String(name).startsWith('apiHubee') && !hubeeUpdated)
        setHubeeUpdated(true);
    },
    [updated, hubeeUpdated],
  );

  const handleChangeInput = React.useCallback(
    (_e, { name, value, checked }) => {
      handleChange(name, checked ?? value);
    },
    [handleChange],
  );

  const handleData = React.useCallback(() => {
    setLoading(true);
    const body: ParametresGeneraux = parametres;
    body.identifiantEntite = selectedEntite?.identifiant;

    if (_.isNil(imageCachet) || _.isEmpty(imageCachet)) {
      body.stockageObjetCachet = null;
    }

    if (_.isNil(imageSignature) || _.isEmpty(imageSignature)) {
      body.stockageObjetSignature = null;
    }

    return new Promise((resolve, reject) => {
      if (!_.isNil(parametres?.identifiant)) {
        request
          .put(updateParametresGeneraux(parametres?.identifiant), body)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        request
          .post(createParametresGeneraux(), body)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      }
    });
  }, [imageCachet, imageSignature, parametres, selectedEntite?.identifiant]);

  const handleImageUpload = React.useCallback((name, file, identifiant) => {
    request.post(uploadFileParametragesGeneraux(identifiant, name), { file });
  }, []);

  const handleSave = React.useCallback(() => {
    const collectiviteErrors = validationTabCollectivite(
      parametres,
      departement,
    );
    const recensementErrors = validationTabRecensement(parametres);

    const allErrors = { ...collectiviteErrors, ...recensementErrors };

    if (!_.isEmpty(allErrors)) {
      setErrors(allErrors);

      // Redirection vers l'onglet contenant des erreurs
      if (
        activeTabIndex === 0 &&
        _.isEmpty(collectiviteErrors) &&
        !_.isEmpty(recensementErrors)
      ) {
        setActiveTabIndex(1);
      } else if (
        activeTabIndex === 1 &&
        _.isEmpty(recensementErrors) &&
        !_.isEmpty(collectiviteErrors)
      ) {
        setActiveTabIndex(0);
      }
      return;
    }

    setErrors({});

    handleData()
      .then(async (response: ParametresGeneraux) => {
        const identifiantParam = response?.identifiant;

        if (
          !_.isNil(imageCachet) &&
          !_.isEmpty(imageCachet) &&
          imageCachet !== initialCachet
        ) {
          await handleImageUpload('cachet', imageCachet, identifiantParam);
        }

        if (
          !_.isNil(imageSignature) &&
          !_.isEmpty(imageSignature) &&
          imageSignature !== initialSignature
        ) {
          await handleImageUpload(
            'signature',
            imageSignature,
            identifiantParam,
          );
        }

        setUpdated(false);
        setHubeeUpdated(false);
        onClose();
        if (redirect) {
          history.push(baseUrlApp);
        }

        // Ping Hubee puis rafraîchir le status après modification des paramètres Hubee uniquement
        if (hubeeUpdated) {
          const pingResponse = (await request.get(pingHubee())) as {
            error: string | null;
          };
          if (pingResponse?.error) {
            toast(pingResponse.error, { type: 'error', autoClose: false });
          }
          fetchHubee.fetchHubeeStatus();
        }

        toast('Enregistrement des paramètres effectué avec succès', {
          type: 'success',
        });
      })
      .catch(() => {
        toast("Erreur lors de l'enregistrement des paramètres", {
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    parametres,
    departement,
    redirect,
    history,
    handleData,
    handleImageUpload,
    initialCachet,
    initialSignature,
    onClose,
    imageCachet,
    imageSignature,
    activeTabIndex,
    fetchHubee,
    hubeeUpdated,
  ]);

  const panes = [
    {
      menuItem: {
        content: 'Collectivité',
        disabled: gestionTrimestre,
      },
      render: () => (
        <TabCollectivite
          parametres={parametres}
          onChange={handleChangeInput}
          loadingImages={loadingImages}
          errors={errors}
        />
      ),
    },
    {
      menuItem: 'Recensement',
      render: () => (
        <TabRecensement
          parametres={parametres}
          onChange={handleChangeInput}
          errors={errors}
        />
      ),
    },
    {
      menuItem: 'HUBEE',
      render: () => (
        <TabHubee parametres={parametres} onChange={handleChangeInput} />
      ),
    },
  ];

  return (
    <Modal
      closeIcon
      colorHeader="black"
      open
      onClose={onClose}
      closeOnDimmerClick={false}
      data-testid="modal-parametres-generaux"
    >
      <Modal.Header>
        <Icon name="cogs" />
        Paramètres généraux
      </Modal.Header>
      <Modal.Content scrolling={false}>
        {!_.isNil(errorMessage) && (
          <Segment
            basic
            style={{
              marginLeft: '-0.5em',
              padding: 0,
            }}
          >
            <Label color="red" ribbon>
              {errorMessage}
            </Label>
          </Segment>
        )}
        <Container style={{ height: 690 }}>
          {loading ? (
            <Dimmer active inverted>
              <Loader inverted>Chargement des paramètres</Loader>
            </Dimmer>
          ) : (
            <Tab
              panes={panes}
              activeIndex={activeTabIndex}
              onTabChange={(
                _e: React.SyntheticEvent<HTMLDivElement>,
                { activeIndex }: { activeIndex: number },
              ) => setActiveTabIndex(activeIndex as number)}
            />
          )}
          {/* {showConfirmationModal && (
            renderModalConfirmation
          )} */}
        </Container>
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-testid="parametres-generaux-cancel-button"
          basic
          size="tiny"
          icon="cancel"
          content="Annuler"
          onClick={onClose}
        />
        <Button
          data-testid="parametres-generaux-validate-button"
          positive
          icon="check"
          content="Enregistrer"
          onClick={handleSave}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalParametresGeneraux;
